import React from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { useGetCurrency } from 'hooks/useGetCurrency';

import { FormTableExpected } from 'components/_basic/Form/FormTableComponent/components/FormTableExpected';
import {
  UNIT_TO_VALUE_TYPE_MAP,
  UNITS,
} from 'components/_basic/Form/FormTableComponent/components/FormTableExpected/constants';
import { useHandleTotalCalculation } from 'components/BusinessToolForm/hooks/useHandleTotalCalculation';

import { formatMonetary } from 'helpers/numbers';
import { concatWithDot } from 'helpers/string';

import { IRAndDTotalProps } from './types';
import { ExpensesFormTotalRow } from '../../../components';
import { useHandleTotalPercentCalculation } from '../../../hooks';
import { MONTHLY_EXPENSES_FORM_CHUNKS, MONTHLY_EXPENSES_FORM_NAMES } from '../../../MonthlyExpensesForm/utils/form';
import { monthlyExpensesPrefix, rAndDPrefix } from '../../../utils/form';
import { R_AND_D_FORM_NAMES } from '../../utils/form';

export const RAndDTotal: React.FC<IRAndDTotalProps> = ({
  name,
  columns,
  firstColumnWidth,
  disabled,
  onErrorStateChange,
  scrollToElement,
}) => {
  const { t } = useTranslation();
  const currency = useGetCurrency();

  const monthlyExpenseFieldName = concatWithDot(
    monthlyExpensesPrefix,
    MONTHLY_EXPENSES_FORM_NAMES[MONTHLY_EXPENSES_FORM_CHUNKS.R_AND_D].EXPENSES,
  );

  const annualExpenseGrowthRateFieldName = concatWithDot(
    monthlyExpensesPrefix,
    MONTHLY_EXPENSES_FORM_NAMES[MONTHLY_EXPENSES_FORM_CHUNKS.R_AND_D].GROWTH_RATE,
  );

  const tableTotalName = concatWithDot(rAndDPrefix, R_AND_D_FORM_NAMES.R_AND_D_TABLE_TOTAL);

  const plannedMonthlyExpense = useWatch({ name: monthlyExpenseFieldName });
  const plannedAnnualExpenseGrowthRate = useWatch({ name: annualExpenseGrowthRateFieldName });

  const formTableExpectedColumnValue: string[] = [
    formatMonetary(plannedMonthlyExpense || 0, currency.value),
    `${plannedAnnualExpenseGrowthRate || 0}${UNIT_TO_VALUE_TYPE_MAP[UNITS.PERCENTAGE]}`,
  ];

  const handleTotalCalculate = useHandleTotalCalculation(tableTotalName);

  const saveWeightedAveragePercent = useHandleTotalPercentCalculation(annualExpenseGrowthRateFieldName);

  return (
    <Grid container gap={1.5} marginTop={1.5}>
      <ExpensesFormTotalRow
        name={name}
        columns={columns}
        expectedTotals={[plannedMonthlyExpense, plannedAnnualExpenseGrowthRate]}
        firstColumnWidth={firstColumnWidth}
        disabled={disabled}
        onErrorStateChange={onErrorStateChange}
        onTotalCalculate={handleTotalCalculate}
        onTotalPercentCalculate={saveWeightedAveragePercent}
      />
      <FormTableExpected
        rowHeader={t('financialPlan.plannedExpenses.rAndD.expectedRowTitle')}
        onHeaderClick={scrollToElement}
        firstColumnWidth={firstColumnWidth}
        columns={formTableExpectedColumnValue}
      />
    </Grid>
  );
};
